import { useImage } from "@hooks/useImage";
import { useRichText } from "@hooks/useRichText";
import { useRoutes } from "@hooks/useRoutes";
import {
  type ComponentProps,
  type FromSanityGraphQL,
  type ResolvedLink,
} from "@ts/components";
import {
  type SectionTestimonialSlider,
  type TestimonialSection,
} from "@ts/sanity";
import React, { type FC } from "react";

type TestimonialInputProps = ComponentProps &
  FromSanityGraphQL<SectionTestimonialSlider>;

type SectionLink = TestimonialSection & {
  sectionLink: ResolvedLink;
};

type TestimonialOutputProps = TestimonialInputProps & {
  sectionLinks: SectionLink[];
  sliderAutoDelay: { delay: number } | boolean;
};

export const withTestimonialSlider =
  (Component: FC<TestimonialOutputProps>) =>
  ({
    name = "TestimonialSlider",
    sections,
    autoDelay,
    ...rest
  }: TestimonialInputProps) => {
    const { linkResolver } = useRoutes();
    const { getFluidImage } = useImage();
    const { parseContent } = useRichText();
    const sliderAutoDelay: { delay: number } | boolean =
      Number(autoDelay) > 0 ? { delay: Number(autoDelay) } : false;
    const sectionLinks = sections?.map((section: TestimonialSection) => ({
      ...section,
      sectionLink: linkResolver(section.link?.[0]),
      content: parseContent(section.content),
      image: getFluidImage(section.image),
    }));

    Component.displayName = name;

    if (!sectionLinks?.length) {
      return null;
    }

    return (
      <Component
        {...rest}
        sectionLinks={sectionLinks}
        sliderAutoDelay={sliderAutoDelay}
      />
    );
  };
